import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useCxcFiliales } from "../hooks/useCxcFiliales";
import PdfEstadoModal from "../components/PdfEstadoModal";
import { formatCurrency } from "../../../../shared/utils/formatCurrency";

// ── Reutilizamos los mismos estilos de facturas ──────────────────────────
import invoiceStyles from "../../invoices/styles/invoice.module.css";

export default function ListCxC() {
  const { t } = useTranslation();

  const FILTROS = [
    { value: "debiendo", label: t("cxc.filtroConSaldo"), icon: "fa-exclamation-circle" },
    { value: "aldia",    label: t("cxc.filtroAlDia"),   icon: "fa-check-circle"        },
    { value: "todos",    label: t("cxc.filtroTodos"),   icon: "fa-list"                },
  ];

  const [filtro, setFiltro] = useState("debiendo");
  const [search, setSearch] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);
  const [pdfModal, setPdfModal] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(1);

  const PAGE_SIZE = 12;
  const { filas, loading } = useCxcFiliales(filtro, search, refreshKey);

  const totalPages = Math.ceil(filas.length / PAGE_SIZE);
  const filasPage = filas.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  function handleSearch(val) {
    setSearch(val);
    setPage(1);
  }

  function handleFiltro(val) {
    setFiltro(val);
    setPage(1);
  }

  const totalSaldo = filas.reduce((a, r) => a + r.totalSaldo, 0);
  const totalMonto = filas.reduce((a, r) => a + r.totalMonto, 0);

  return (
    <>
      {/* ── Breadcrumb ─────────────────────────────────────────────── */}
      <span className={invoiceStyles.inactive}>
        <Link to="/">
          <span className={invoiceStyles.inactive}>{t("common.inicio")} /</span>
        </Link>
        <span className={invoiceStyles.active}> {t("cxc.titulo")}</span>
      </span>

      {/* ── Header ─────────────────────────────────────────────────── */}
      <div
        className="d-flex justify-content-between align-items-start flex-wrap gap-2"
        style={{ marginBottom: 6 }}
      >
        <div>
          <h2 className="mt-2 me-2 p-0 title">{t("cxc.gestion")}</h2>
          <p className="mb-3" style={{ color: "var(--text-muted)" }}>{t("cxc.administra")}</p>
        </div>
      </div>

      {/* ── Cards resumen ──────────────────────────────────────────── */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
          gap: 12,
          marginBottom: 20,
        }}
      >
        <SummaryCard
          label={t("cxc.filialesConSaldo")}
          value={filas.filter((r) => r.totalSaldo > 0).length}
          icon="fa-house-circle-exclamation"
          color="#f97316"
          isCurrency={false}
        />
        <SummaryCard
          label={t("cxc.totalFacturado")}
          value={totalMonto}
          icon="fa-file-invoice-dollar"
          color="#60a5fa"
        />
        <SummaryCard
          label={t("cxc.totalPendiente")}
          value={totalSaldo}
          icon="fa-money-bill-wave"
          color={totalSaldo > 0 ? "#f87171" : "#4ade80"}
        />
      </div>

      {/* ── Tabla principal ────────────────────────────────────────── */}
      <div className={invoiceStyles["table-container"]}>
        {/* Barra de búsqueda + filtros */}
        <div
          className={`${invoiceStyles["table-header"]} d-flex justify-content-between`}
        >
          {/* Búsqueda */}
          <div className={`${invoiceStyles["input-header"]} d-flex gap-2`}>
            <i
              className="fa-solid fa-magnifying-glass"
              style={{
                color: loading ? "#f97316" : undefined,
                transition: "color 0.2s",
              }}
            />
            <input
              type="text"
              placeholder={t("cxc.buscarPlaceholder")}
              value={search}
              onChange={(e) => handleSearch(e.target.value)}
            />
            {search && (
              <button
                onClick={() => handleSearch("")}
                style={{
                  background: "none",
                  border: "none",
                  color: "var(--text-muted)",
                  cursor: "pointer",
                  fontSize: "0.8rem",
                }}
              >
                ✕
              </button>
            )}
          </div>

          {/* Botones filtro / tabs */}
          <div
            className={`${invoiceStyles["actions-buttons-header"]} d-flex gap-2`}
          >
            <button
              className={`btn ${invoiceStyles["btn-filter"]} d-flex gap-2 align-items-center`}
              onClick={() => setShowFilters((v) => !v)}
            >
              <i className="fa-solid fa-filter" />
              {FILTROS.find((f) => f.value === filtro)?.label ?? t("common.filtros")}
            </button>
          </div>
        </div>

        {/* Panel de filtros */}
        {showFilters && (
          <div
            style={{
              padding: "12px 16px",
              borderBottom: "1px solid var(--border-card)",
              display: "flex",
              gap: 10,
              flexWrap: "wrap",
            }}
          >
            {FILTROS.map((f) => (
              <button
                key={f.value}
                onClick={() => {
                  handleFiltro(f.value);
                  setShowFilters(false);
                }}
                style={{
                  padding: "6px 14px",
                  borderRadius: 8,
                  border:
                    filtro === f.value
                      ? "1px solid #f97316"
                      : "1px solid var(--border-card)",
                  background:
                    filtro === f.value
                      ? "rgba(249,115,22,0.15)"
                      : "transparent",
                  color: filtro === f.value ? "#f97316" : "var(--text-secondary)",
                  fontSize: "0.83rem",
                  fontWeight: filtro === f.value ? 600 : 400,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  transition: "all 0.15s",
                }}
              >
                <i
                  className={`fa-solid ${f.icon}`}
                  style={{ fontSize: "0.75rem" }}
                />
                {f.label}
              </button>
            ))}
          </div>
        )}

        {/* Tabla */}
        <div
          className={`${invoiceStyles["table-body"]} ${invoiceStyles["table-responsive"]}`}
        >
          <table>
            <thead>
              <tr>
                <th>{t("common.filial")}</th>
                <th>{t("cxc.propietario")}</th>
                <th className="text-end">{t("cxc.totalFacturadoCol")}</th>
                <th className="text-end">{t("cxc.totalAbonadoCol")}</th>
                <th className="text-end">{t("cxc.saldoAcumulado")}</th>
                <th className="text-end">{t("common.estado")}</th>
                <th className="text-center">{t("common.acciones")}</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan={7}
                    style={{
                      textAlign: "center",
                      padding: "32px",
                      color: "var(--text-secondary)",
                    }}
                  >
                    <i
                      className="fa-solid fa-circle-notch fa-spin"
                      style={{ marginRight: 8, color: "#f97316" }}
                    />
                    {t("cxc.cargando")}
                  </td>
                </tr>
              ) : filasPage.length === 0 ? (
                <tr>
                  <td
                    colSpan={7}
                    style={{
                      textAlign: "center",
                      padding: "32px",
                      color: "var(--text-muted)",
                    }}
                  >
                    <i
                      className="fa-solid fa-inbox"
                      style={{ marginRight: 8, fontSize: "1.2rem" }}
                    />
                    {t("cxc.sinFiliales")}
                  </td>
                </tr>
              ) : (
                filasPage.map((row, idx) => (
                  <CxCRow
                    key={row.filialId}
                    row={row}
                    idx={idx}
                    onPdf={(filialId, idFilial) =>
                      setPdfModal({ id: filialId, idFilial })
                    }
                  />
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Footer con paginación */}
        <div className={invoiceStyles["table-footer"]}>
          <p>
            {loading ? (
              <span style={{ color: "#f97316", fontSize: "0.83rem" }}>
                <i
                  className="fa-solid fa-circle-notch fa-spin"
                  style={{ marginRight: 5 }}
                />
                {t("common.cargandoDatos")}
              </span>
            ) : (
              <>
                <span>{filas.length}</span> {t("cxc.filiales")} · {t("common.pagina")}{" "}
                <span>{page}</span> {t("common.de")} <span>{Math.max(1, totalPages)}</span>
              </>
            )}
          </p>
          <div
            className={`${invoiceStyles["pagination-buttons"]} d-flex gap-2`}
          >
            <button
              disabled={page <= 1 || loading}
              onClick={() => setPage((p) => p - 1)}
              className={`btn btn-sm ${invoiceStyles["btn-prev"]} d-flex gap-2 align-items-center`}
            >
              {t("common.anterior")}
            </button>
            <button
              disabled={page >= totalPages || loading}
              onClick={() => setPage((p) => p + 1)}
              className={`btn btn-sm ${invoiceStyles["btn-next"]} d-flex gap-2 align-items-center`}
            >
              {t("common.siguiente")}
            </button>
          </div>
        </div>
      </div>

      {/* ── Modal PDF ──────────────────────────────────────────────── */}
      {pdfModal !== null && (
        <PdfEstadoModal
          onClose={() => setPdfModal(null)}
          filialPreselected={pdfModal.id ? pdfModal : null}
        />
      )}
    </>
  );
}

// ── Fila de la tabla ────────────────────────────────────────────────────

function CxCRow({ row, onPdf }) {
  const { t } = useTranslation();
  const saldo = row.totalSaldo;
  const alDia = saldo <= 0;
  const parcial = !alDia && row.totalAbono > 0;

  return (
    <tr>
      <td style={{ fontWeight: 600, color: "var(--text-primary)" }}>{row.filialLabel}</td>
      <td>{row.propietario || <span style={{ color: "var(--text-dim)" }}>—</span>}</td>
      <td className="text-end">₡{formatCurrency(row.totalMonto)}</td>
      <td
        className="text-end"
        style={{ color: row.totalAbono > 0 ? "#4ade80" : "var(--text-secondary)" }}
      >
        ₡{formatCurrency(row.totalAbono)}
      </td>
      <td
        className="text-end"
        style={{ fontWeight: 600, color: alDia ? "#4ade80" : "#f87171" }}
      >
        ₡{formatCurrency(Math.abs(saldo))}
        {saldo < 0 && (
          <span style={{ fontSize: "0.72rem", marginLeft: 4 }}>{t("cxc.aFavor")}</span>
        )}
      </td>

      {/* Estado */}
      <td className="text-end">
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 5,
            padding: "3px 10px",
            borderRadius: 20,
            fontSize: "0.74rem",
            fontWeight: 600,
            background: alDia
              ? "rgba(74,222,128,0.1)"
              : parcial
                ? "rgba(251,191,36,0.1)"
                : "rgba(248,113,113,0.1)",
            color: alDia ? "#4ade80" : parcial ? "#fbbf24" : "#f87171",
            border: `1px solid ${alDia ? "rgba(74,222,128,0.25)" : parcial ? "rgba(251,191,36,0.25)" : "rgba(248,113,113,0.25)"}`,
          }}
        >
          <i
            className={`fa-solid ${alDia ? "fa-check" : parcial ? "fa-clock" : "fa-exclamation"}`}
            style={{ fontSize: "0.65rem" }}
          />
          {alDia ? t("cxc.alDia") : parcial ? t("common.parcial") : t("common.pendiente")}
        </span>
      </td>

      {/* Acciones */}
      <td className="d-flex text-center justify-content-center">
        <button
          title={t("cxc.estadoPDF")}
          onClick={() => onPdf(row.filialId, row.filialLabel)}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 5,
            padding: "4px 12px",
            background: "transparent",
            border: "1px solid rgba(249,115,22,0.3)",
            borderRadius: 20,
            color: "#f97316",
            fontSize: "0.76rem",
            fontWeight: 600,
            cursor: "pointer",
            transition: "all 0.15s",
            whiteSpace: "nowrap",
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.background = "rgba(249,115,22,0.15)";
            e.currentTarget.style.borderColor = "#f97316";
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.background = "transparent";
            e.currentTarget.style.borderColor = "rgba(249,115,22,0.3)";
          }}
        >
          <i className="fa-solid fa-file-pdf" style={{ fontSize: "0.7rem" }} />
          {t("cxc.estadoPDF")}
        </button>
      </td>
    </tr>
  );
}

// ── Card de resumen ──────────────────────────────────────────────────────

function SummaryCard({ label, value, icon, color, isCurrency = true }) {
  return (
    <div
      style={{
        background: "var(--bg-card)",
        border: "1px solid var(--border-card)",
        borderRadius: 10,
        padding: "16px 20px",
        display: "flex",
        alignItems: "center",
        gap: 14,
      }}
    >
      <div
        style={{
          width: 42,
          height: 42,
          borderRadius: 10,
          flexShrink: 0,
          background: `${color}18`,
          border: `1px solid ${color}30`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <i
          className={`fa-solid ${icon}`}
          style={{ color, fontSize: "1.1rem" }}
        />
      </div>
      <div>
        <p
          style={{
            margin: 0,
            color: "var(--text-muted)",
            fontSize: "0.72rem",
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: "0.4px",
          }}
        >
          {label}
        </p>
        <p
          style={{
            margin: "2px 0 0",
            color: "var(--text-primary)",
            fontSize: "1.1rem",
            fontWeight: 700,
          }}
        >
          {isCurrency ? `₡${formatCurrency(value)}` : value}
        </p>
      </div>
    </div>
  );
}
